/**
 * Multiview renderer — one WebGL canvas, many feeds (program, preview, monitor wall).
 *
 * Tiers:
 *  - program: full device-pixel resolution, MSAA, rendered every frame. Goes through
 *    an optional "broadcast look" post pass (mild unsharp mask + vignette + a touch
 *    of contrast) or a caller-supplied post hook.
 *  - preview: rendered directly into its scissor rect every frame at full quality.
 *  - wall: rendered into a small per-camera RenderTarget (MSAA, linear-filtered) at
 *    min(devicePixelRatio, `wallPixelRatio`) × the tile's CSS size — i.e. at (or close to)
 *    the tile's real device-pixel size, so tiles read as crisp little monitors rather than
 *    upscaled half-res blocks. Refreshed round-robin — each wall
 *    tile updates every `wallInterval` frames, staggered so the cost is spread
 *    evenly. EVERY frame the cached textures are blitted (one textured quad each),
 *    so tiles never go blank. We deliberately do NOT rely on "don't clear the
 *    scissor region" because a default WebGL canvas (preserveDrawingBuffer=false)
 *    discards its contents after compositing; the RT cache is robust and also
 *    gives us the reduced resolution for free.
 *
 * Program extras (all driven by `scene.userData.sdPost`, see `BroadcastPost`, so any
 * scene/world can opt in without the caller wiring anything): a cheap HDR bloom
 * (threshold → ¼-res + ⅛-res separable blurs) and a broadcast colour grade.
 * Adaptive resolution: if real frame intervals stay slow, the program RT's render
 * scale steps down (never below 0.6) — the canvas/UI stay sharp, only the 3D gets softer.
 *
 * Every camera gets `camera.userData.sdTier` set to the feed tier before its scene render,
 * so scene objects can adapt per feed (e.g. weather particles thin out on wall tiles).
 *
 * Rects are CSS pixels relative to the canvas, origin top-left.
 * Colour management: scene renders into HalfFloat RTs (linear, no tone mapping);
 * the blit/post materials apply the renderer's tone mapping + output colour space,
 * so RT-routed feeds match direct-rendered ones.
 */
import * as THREE from 'three';
import type { CameraCrew, FeedSource } from './operators';

export type FeedTier = 'program' | 'preview' | 'wall';

export interface FeedRect { x: number; y: number; w: number; h: number }

export interface FeedLayout {
  camId: string;
  rect: FeedRect;
  tier: FeedTier;
  /** 'replay' renders the crew's replay camera for this camId (pose via crew.replayCamera). */
  source?: FeedSource;
  /**
   * Program tier only: cross-dissolve with a second feed (MIX transition).
   * Final image = this feed * (1 - weight) + blend feed * weight.
   */
  blend?: { camId: string; source?: FeedSource; weight: number };
}

/**
 * Custom program post-process. Receives the program image (linear HDR texture) and
 * must draw it into the currently bound viewport (canvas, scissored to the rect).
 */
export type ProgramPostHook = (renderer: THREE.WebGLRenderer, input: THREE.Texture, rect: FeedRect) => void;

/** Per-scene program look, read from `scene.userData.sdPost` every frame. */
export interface BroadcastPost {
  /** Bloom amount (0 = off). */
  bloom?: number;
  /** Linear-HDR luminance where bloom starts. */
  bloomThreshold?: number;
  /** Soft knee width around the threshold. */
  bloomKnee?: number;
  /** 1 = unchanged. */
  saturation?: number;
  /** 1 = unchanged (pivot at mid grey, applied before tone mapping). */
  contrast?: number;
  /** Multiplicative colour balance (linear). */
  tint?: [number, number, number];
}

export interface WallQuality {
  /** Max device-pixel ratio the wall tiles render at (capped by the real dpr). */
  pixelRatio: number;
  /** MSAA samples for wall tile RTs (0 = off). */
  samples: number;
  /** Refresh each tile every N frames. */
  interval?: number;
}

/** Sensible wall settings per world quality. */
export function wallQualityFor(q: 'low' | 'medium' | 'high'): WallQuality {
  if (q === 'low') return { pixelRatio: 1, samples: 2, interval: 4 };
  if (q === 'medium') return { pixelRatio: 1.25, samples: 4, interval: 3 };
  return { pixelRatio: 2, samples: 4, interval: 3 };
}

export interface MultiviewOptions {
  /** Legacy: wall tile resolution relative to device pixels. Ignored when `wall` is given. */
  wallScale?: number;
  /** Wall tile resolution / AA / refresh (default `wallQualityFor('high')`). */
  wall?: WallQuality;
  /** Adaptive program resolution from measured frame intervals (default true). */
  adaptive?: boolean;
  /** Refresh each wall tile every N frames (default 2). 1 = every frame. */
  wallInterval?: number;
  /** Built-in broadcast look on the program feed (default true). */
  broadcastLook?: boolean;
  /** MSAA samples for the program RT (default 4). */
  programSamples?: number;
  /**
   * Called right before each feed's scene render. Use it to pose the shared scene for
   * that feed — e.g. apply the historical snapshot for `source: 'replay'` feeds and the
   * live one otherwise (cheap: only transforms change), LOD switches, hiding rigs.
   */
  onBeforeFeed?: (feed: FeedLayout, camera: THREE.PerspectiveCamera) => void;
  /** Background clear colour for the whole canvas. */
  clearColor?: THREE.ColorRepresentation;
}

export interface MultiviewStats {
  frame: number;
  /** Scene renders this frame (the expensive part). */
  sceneRenders: number;
  blits: number;
}

export interface Multiview {
  /** Draw all feeds. Clears the whole canvas, so call once per frame with every feed. */
  render(layout: readonly FeedLayout[]): MultiviewStats;
  setProgramPost(hook: ProgramPostHook | null): void;
  setWallQuality(q: WallQuality): void;
  /** Current adaptive program render scale (1 = full res). */
  readonly programScale: number;
  setBroadcastLook(on: boolean): void;
  /** Force all wall tiles to refresh next frame (e.g. after a scene change). */
  invalidateWall(): void;
  dispose(): void;
}

const POST_FRAG = /* glsl */ `
uniform sampler2D tInput;
uniform sampler2D tBlend;
uniform sampler2D tBloom1;
uniform sampler2D tBloom2;
uniform float blendW;
uniform vec2 texel;
uniform float sharpen;
uniform float vignette;
uniform float bloom;
uniform float saturation;
uniform float contrast;
uniform vec3 tint;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(tInput, vUv).rgb;
  vec3 n = texture2D(tInput, vUv + vec2(texel.x, 0.0)).rgb + texture2D(tInput, vUv - vec2(texel.x, 0.0)).rgb
         + texture2D(tInput, vUv + vec2(0.0, texel.y)).rgb + texture2D(tInput, vUv - vec2(0.0, texel.y)).rgb;
  c = max(c + (c - n * 0.25) * sharpen, 0.0);
  vec2 d = vUv - 0.5;
  if (blendW > 0.0) c = mix(c, texture2D(tBlend, vUv).rgb, blendW);
  if (bloom > 0.0) c += (texture2D(tBloom1, vUv).rgb * 0.55 + texture2D(tBloom2, vUv).rgb * 0.75) * bloom;
  c *= tint;
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = max(mix(vec3(l), c, saturation), 0.0);
  c = pow(c / 0.18 + 1e-6, vec3(contrast)) * 0.18;
  c *= 1.0 - vignette * smoothstep(0.35, 0.85, length(d * vec2(1.0, 0.8)));
  gl_FragColor = vec4(c, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

const QUAD_VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

/** Bright-pass + 4×4 box downsample with a soft knee (linear HDR in, linear out). */
const BRIGHT_FRAG = /* glsl */ `
uniform sampler2D tInput;
uniform vec2 texel;
uniform float threshold;
uniform float knee;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(tInput, vUv + texel * vec2(-1.0, -1.0)).rgb + texture2D(tInput, vUv + texel * vec2(1.0, -1.0)).rgb
         + texture2D(tInput, vUv + texel * vec2(-1.0, 1.0)).rgb + texture2D(tInput, vUv + texel * vec2(1.0, 1.0)).rgb;
  c *= 0.25;
  c = min(c, vec3(24.0));
  float br = max(c.r, max(c.g, c.b));
  float soft = clamp(br - threshold + knee, 0.0, 2.0 * knee);
  soft = soft * soft / (4.0 * knee + 1e-4);
  float w = max(soft, br - threshold) / max(br, 1e-4);
  gl_FragColor = vec4(c * w, 1.0);
}`;

/** 9-tap gaussian in 5 bilinear fetches. */
const BLUR_FRAG = /* glsl */ `
uniform sampler2D tInput;
uniform vec2 dir;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(tInput, vUv).rgb * 0.2270270270;
  c += texture2D(tInput, vUv + dir * 1.3846153846).rgb * 0.3162162162;
  c += texture2D(tInput, vUv - dir * 1.3846153846).rgb * 0.3162162162;
  c += texture2D(tInput, vUv + dir * 3.2307692308).rgb * 0.0702702703;
  c += texture2D(tInput, vUv - dir * 3.2307692308).rgb * 0.0702702703;
  gl_FragColor = vec4(c, 1.0);
}`;

export function createMultiview(renderer: THREE.WebGLRenderer, scene: THREE.Scene, crew: CameraCrew, opts: MultiviewOptions = {}): Multiview {
  const legacyScale = opts.wall ? null : opts.wallScale ?? null;
  let wallQ: WallQuality = opts.wall ?? wallQualityFor('high');
  let wallInterval = Math.max(1, Math.round(opts.wall?.interval ?? opts.wallInterval ?? 2));
  const adaptive = opts.adaptive ?? true;
  let broadcastLook = opts.broadcastLook ?? true;
  let postHook: ProgramPostHook | null = null;
  const clearColor = new THREE.Color(opts.clearColor ?? 0x050607);

  // Fullscreen quad for blits / post.
  const quadScene = new THREE.Scene();
  const quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const blitMat = new THREE.MeshBasicMaterial({ depthTest: false, depthWrite: false });
  const postMat = new THREE.ShaderMaterial({
    uniforms: {
      tInput: { value: null }, tBlend: { value: null }, tBloom1: { value: null }, tBloom2: { value: null }, blendW: { value: 0 },
      texel: { value: new THREE.Vector2() }, sharpen: { value: 0.35 }, vignette: { value: 0.22 },
      bloom: { value: 0 }, saturation: { value: 1 }, contrast: { value: 1 }, tint: { value: new THREE.Vector3(1, 1, 1) },
    },
    vertexShader: QUAD_VERT, fragmentShader: POST_FRAG, depthTest: false, depthWrite: false,
  });
  const brightMat = new THREE.ShaderMaterial({
    uniforms: { tInput: { value: null }, texel: { value: new THREE.Vector2() }, threshold: { value: 1 }, knee: { value: 0.5 } },
    vertexShader: QUAD_VERT, fragmentShader: BRIGHT_FRAG, depthTest: false, depthWrite: false,
  });
  const blurMat = new THREE.ShaderMaterial({
    uniforms: { tInput: { value: null }, dir: { value: new THREE.Vector2() } },
    vertexShader: QUAD_VERT, fragmentShader: BLUR_FRAG, depthTest: false, depthWrite: false,
  });
  // bloom chain: [¼ ping, ¼ pong, ⅛ ping, ⅛ pong]
  const bloomRTs: THREE.WebGLRenderTarget[] = [];
  const ensureBloom = (w: number, h: number) => {
    const dims = [[w >> 2, h >> 2], [w >> 2, h >> 2], [w >> 3, h >> 3], [w >> 3, h >> 3]] as const;
    dims.forEach(([bw, bh], i) => {
      const W = Math.max(8, bw), H = Math.max(8, bh);
      const rt = bloomRTs[i];
      if (!rt) bloomRTs[i] = new THREE.WebGLRenderTarget(W, H, { type: THREE.HalfFloatType, depthBuffer: false, colorSpace: THREE.LinearSRGBColorSpace });
      else if (rt.width !== W || rt.height !== H) rt.setSize(W, H);
    });
  };
  const fullscreen = (target: THREE.WebGLRenderTarget, mat: THREE.Material) => {
    renderer.setRenderTarget(target);
    quad.material = mat;
    renderer.render(quadScene, quadCam);
  };
  const runBloom = (src: THREE.WebGLRenderTarget, post: BroadcastPost) => {
    ensureBloom(src.width, src.height);
    const [a, b, c, d] = bloomRTs as [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget, THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
    const bu = brightMat.uniforms;
    bu.tInput!.value = src.texture;
    (bu.texel!.value as THREE.Vector2).set(1 / src.width, 1 / src.height);
    bu.threshold!.value = post.bloomThreshold ?? 1.0;
    bu.knee!.value = post.bloomKnee ?? 0.5;
    fullscreen(a, brightMat);
    const blur = (from: THREE.WebGLRenderTarget, to: THREE.WebGLRenderTarget, x: number, y: number) => {
      blurMat.uniforms.tInput!.value = from.texture;
      (blurMat.uniforms.dir!.value as THREE.Vector2).set(x / from.width, y / from.height);
      fullscreen(to, blurMat);
    };
    blur(a, b, 1, 0); blur(b, a, 0, 1);       // ¼ res result in a
    blur(a, d, 1, 0);                          // ⅛: downsample + H blur (bilinear fetch halves)
    blur(d, c, 0, 1); blur(c, d, 1.6, 0); blur(d, c, 0, 1.6); // wider ⅛ result in c
  };
  const scenePost = (): BroadcastPost => (scene.userData.sdPost as BroadcastPost | undefined) ?? {};

  // Adaptive program resolution: watch real frame intervals (rAF cadence).
  let programScale = 1;
  let lastT = 0, slowAcc = 0, fastAcc = 0, smoothDt = 16.7, warm = 0;
  const adapt = () => {
    if (!adaptive) return;
    const now = performance.now();
    const dt = lastT ? now - lastT : 0;
    lastT = now;
    if (dt <= 0 || dt > 120) return; // first frame / tab hidden / paused hitch: ignore
    if (warm < 90) { warm++; return; }  // let shaders compile + caches warm
    smoothDt += (dt - smoothDt) * 0.05;
    if (smoothDt > 21) { slowAcc += dt; fastAcc = 0; } else { slowAcc = Math.max(0, slowAcc - dt * 0.5); }
    if (smoothDt < 14 && programScale < 1) fastAcc += dt; else if (smoothDt >= 14) fastAcc = 0;
    if (slowAcc > 2000 && programScale > 0.6) {
      programScale = Math.max(0.6, Math.round((programScale - 0.1) * 100) / 100);
      slowAcc = 0; smoothDt = 16.7;
    } else if (fastAcc > 6000) {
      programScale = Math.min(1, programScale + 0.1);
      fastAcc = 0;
    }
  };
  const quad = new THREE.Mesh<THREE.PlaneGeometry, THREE.Material>(new THREE.PlaneGeometry(2, 2), blitMat);
  quad.frustumCulled = false;
  quad.position.z = -0.5; // inside the ortho camera's [near, far]
  quadScene.add(quad);

  const wallRTs = new Map<string, { rt: THREE.WebGLRenderTarget; lastFrame: number }>();
  let programRT: THREE.WebGLRenderTarget | null = null;
  let blendRT: THREE.WebGLRenderTarget | null = null;
  let frame = 0;
  const size = new THREE.Vector2();
  const prevClear = new THREE.Color();

  const makeRT = (w: number, h: number, samples: number) =>
    new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, samples, depthBuffer: true, colorSpace: THREE.LinearSRGBColorSpace });

  const setRectOnCanvas = (r: FeedRect) => {
    const H = size.y;
    renderer.setViewport(r.x, H - r.y - r.h, r.w, r.h);
    renderer.setScissor(r.x, H - r.y - r.h, r.w, r.h);
  };

  const drawQuad = (mat: THREE.Material) => {
    quad.material = mat;
    renderer.render(quadScene, quadCam);
  };

  const cameraFor = (l: FeedLayout, aspect: number) => {
    const cam = crew.threeCamera(l.camId, l.source ?? 'live');
    if (Math.abs(cam.aspect - aspect) > 1e-4) { cam.aspect = aspect; cam.updateProjectionMatrix(); }
    cam.userData.sdTier = l.tier;
    return cam;
  };

  return {
    render(layout) {
      frame++;
      adapt();
      const stats: MultiviewStats = { frame, sceneRenders: 0, blits: 0 };
      renderer.getSize(size);
      const dpr = renderer.getPixelRatio();
      const prevAutoClear = renderer.autoClear;
      renderer.getClearColor(prevClear);
      const prevAlpha = renderer.getClearAlpha();
      renderer.autoClear = false;

      // ---- pass 1: offscreen renders (wall tiles due for refresh, program if post) ----
      let wallIdx = 0;
      for (const l of layout) {
        if (l.tier !== 'wall') continue;
        const key = `${l.camId}|${l.source ?? 'live'}`;
        const pr = legacyScale !== null ? dpr * legacyScale : Math.min(dpr, wallQ.pixelRatio);
        const w = Math.max(16, Math.round(l.rect.w * pr));
        const h = Math.max(9, Math.round(l.rect.h * pr));
        let entry = wallRTs.get(key);
        const samples = legacyScale !== null ? 0 : wallQ.samples;
        if (entry && entry.rt.samples !== samples) { entry.rt.dispose(); wallRTs.delete(key); entry = undefined; }
        if (!entry) { entry = { rt: makeRT(w, h, samples), lastFrame: -1e9 }; wallRTs.set(key, entry); }
        else if (entry.rt.width !== w || entry.rt.height !== h) { entry.rt.setSize(w, h); entry.lastFrame = -1e9; }
        const due = entry.lastFrame < 0 || (frame + wallIdx) % wallInterval === 0;
        wallIdx++;
        if (!due) continue;
        const cam = cameraFor(l, l.rect.w / l.rect.h);
        opts.onBeforeFeed?.(l, cam);
        renderer.setRenderTarget(entry.rt);
        renderer.setClearColor(clearColor, 1);
        renderer.clear(true, true, false);
        renderer.render(scene, cam);
        entry.lastFrame = frame;
        stats.sceneRenders++;
      }
      const program = layout.find((l) => l.tier === 'program');
      const blend = program?.blend && program.blend.weight > 0.001 && postHook === null ? program.blend : null;
      const usePost = !!program && (broadcastLook || postHook !== null || blend !== null);
      if (program && usePost) {
        const w = Math.max(16, Math.round(program.rect.w * dpr * programScale));
        const h = Math.max(9, Math.round(program.rect.h * dpr * programScale));
        if (!programRT) programRT = makeRT(w, h, opts.programSamples ?? 4);
        else if (programRT.width !== w || programRT.height !== h) programRT.setSize(w, h);
        if (blend) {
          if (!blendRT) blendRT = makeRT(w, h, opts.programSamples ?? 4);
          else if (blendRT.width !== w || blendRT.height !== h) blendRT.setSize(w, h);
          const bl: FeedLayout = { camId: blend.camId, rect: program.rect, tier: 'program', source: blend.source ?? 'live' };
          const bcam = cameraFor(bl, program.rect.w / program.rect.h);
          opts.onBeforeFeed?.(bl, bcam);
          renderer.setRenderTarget(blendRT);
          renderer.setClearColor(clearColor, 1);
          renderer.clear(true, true, false);
          renderer.render(scene, bcam);
          stats.sceneRenders++;
        }
        const cam = cameraFor(program, program.rect.w / program.rect.h);
        opts.onBeforeFeed?.(program, cam);
        renderer.setRenderTarget(programRT);
        renderer.setClearColor(clearColor, 1);
        renderer.clear(true, true, false);
        renderer.render(scene, cam);
        stats.sceneRenders++;
      }
      const post = scenePost();
      const bloomAmt = program && usePost && programRT && postHook === null && broadcastLook ? (post.bloom ?? 0) : 0;
      if (bloomAmt > 0 && programRT) runBloom(programRT, post);

      // ---- pass 2: compose on the canvas ----
      renderer.setRenderTarget(null);
      renderer.setScissorTest(false);
      renderer.setClearColor(clearColor, 1);
      renderer.clear(true, true, false);
      renderer.setScissorTest(true);
      for (const l of layout) {
        if (l.rect.w < 1 || l.rect.h < 1) continue;
        setRectOnCanvas(l.rect);
        if (l.tier === 'wall') {
          const entry = wallRTs.get(`${l.camId}|${l.source ?? 'live'}`);
          if (!entry) continue;
          blitMat.map = entry.rt.texture;
          drawQuad(blitMat);
          stats.blits++;
        } else if (l.tier === 'program' && usePost && programRT) {
          if (postHook) postHook(renderer, programRT.texture, l.rect);
          else {
            postMat.uniforms.tInput!.value = programRT.texture;
            postMat.uniforms.tBlend!.value = blend && blendRT ? blendRT.texture : programRT.texture;
            postMat.uniforms.blendW!.value = blend ? Math.min(1, blend.weight) : 0;
            postMat.uniforms.sharpen!.value = broadcastLook ? 0.35 : 0;
            postMat.uniforms.vignette!.value = broadcastLook ? 0.22 : 0;
            const pu = postMat.uniforms;
            pu.bloom!.value = bloomAmt;
            pu.tBloom1!.value = bloomRTs[0]?.texture ?? programRT.texture;
            pu.tBloom2!.value = bloomRTs[2]?.texture ?? programRT.texture;
            pu.saturation!.value = broadcastLook ? post.saturation ?? 1 : 1;
            pu.contrast!.value = broadcastLook ? post.contrast ?? 1 : 1;
            const t = broadcastLook && post.tint ? post.tint : [1, 1, 1];
            (pu.tint!.value as THREE.Vector3).set(t[0]!, t[1]!, t[2]!);
            (postMat.uniforms.texel!.value as THREE.Vector2).set(1 / programRT.width, 1 / programRT.height);
            drawQuad(postMat);
          }
          stats.blits++;
        } else {
          const cam = cameraFor(l, l.rect.w / l.rect.h);
          opts.onBeforeFeed?.(l, cam);
          renderer.clear(true, true, false); // clears only the scissor rect
          renderer.render(scene, cam);
          stats.sceneRenders++;
        }
      }
      renderer.setScissorTest(false);
      renderer.autoClear = prevAutoClear;
      renderer.setClearColor(prevClear, prevAlpha);
      renderer.setViewport(0, 0, size.x, size.y);
      return stats;
    },
    setProgramPost(hook) { postHook = hook; },
    setWallQuality(q) {
      wallQ = q;
      if (q.interval) wallInterval = Math.max(1, Math.round(q.interval));
      for (const e of wallRTs.values()) e.lastFrame = -1e9;
    },
    get programScale() { return programScale; },
    setBroadcastLook(on) { broadcastLook = on; },
    invalidateWall() { for (const e of wallRTs.values()) e.lastFrame = -1e9; },
    dispose() {
      for (const e of wallRTs.values()) e.rt.dispose();
      wallRTs.clear();
      programRT?.dispose();
      programRT = null;
      blendRT?.dispose();
      blendRT = null;
      for (const rt of bloomRTs.splice(0)) rt.dispose();
      blitMat.dispose(); postMat.dispose(); brightMat.dispose(); blurMat.dispose(); quad.geometry.dispose();
    },
  };
}
